import React, { useMemo, useState, forwardRef } from 'react'
import {
  useTable,
  useSortBy,
  useGlobalFilter,
  usePagination,
  useRowSelect,
  useColumnOrder,
  useBlockLayout,
} from 'react-table'
import { FixedSizeList } from 'react-window'
import AutoSizer from 'react-virtualized-auto-sizer'
import DashboardIcon from '../DashboardIcon'
import ColumnHiding from './ColumnHiding'
import Modal from '../../layouts/Modal'
import ArrowUp from '../../assets/arrow_up_icon.png'
import ArrowDown from '../../assets/arrow_down_icon.png'

// DATA
// import MOCK_DATA from './NewCampaignData.json'

// CSS
import '../../layouts/stylesheets/table.css'
import '../../layouts/stylesheets/tooltip.css'
// import Settings from '../../assets/settings.png'

const splitKeyedProps = (propsWithKey = {}, className) => {
  const { key, className: propClassName, ...rest } = propsWithKey
  const classNames = [className, propClassName].filter(Boolean).join(' ')
  return {
    key,
    props: {
      ...rest,
      ...(classNames ? { className: classNames } : {}),
    },
  }
}

const Table = (props) => {
  const columns = useMemo(() => props.columns, [props.columns])
  const data = props.data
  const dashboardActive = useMemo(
    () => props.dashboardActive,
    [props.dashboardActive]
  )
  const [isModalOpen, setIsModalOpen] = useState(false)
  const widthProps = props.width

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    allColumns,
    footerGroups,
  } = useTable(
    {
      columns,
      data,
      initialState: {
        hiddenColumns: columns
          .filter((column) => column?.hide)
          .map((column) => column.accessor),
        columnOrder: props.order,
      },
    },
    useGlobalFilter,
    useSortBy,
    usePagination,
    useRowSelect,
    useColumnOrder,
    useBlockLayout,
    (hooks) => {
      if (dashboardActive) {
        hooks.allColumns.push((columns) => [
          {
            id: 'dashboard',
            justifyContent: 'center',
            hide: false,
            hiddenHeader: true,
            tooltip: true,
            tooltipText: 'Go to dashboard',
            width: '100%',
            Cell: ({ row }) => (
              <DashboardIcon
                id={row.original.id}
                {...row.getToggleRowSelectedProps()}
              />
            ),
          },
          ...columns,
        ])
      }
    }
  )

  const shownRows = props.showCount ? rows.slice(0, props.showCount) : rows

  const innerElementType = forwardRef(({ style, ...rest }, ref) => {
    const { key, props: innerProps } = splitKeyedProps(rest)
    return (
      <div
        key={key}
        ref={ref}
        style={{
          ...style,
          height: `${parseFloat(style.height) + props.topOffset * 2}px`,
        }}
        {...innerProps}
      />
    )
  })

  const RenderRow = React.useCallback(
    ({ index, style }) => {
      const row = shownRows[index]
      if (props.topOffset) {
        style.top = index * style.height + props.topOffset
      }
      prepareRow(row)
      const { key: rowKey, props: rowProps } = splitKeyedProps(
        row.getRowProps({
          style,
        }),
        'tr'
      )
      return (
        <div key={rowKey} {...rowProps}>
          {row.cells.map((cell) => {
            const { key: cellKey, props: cellProps } = splitKeyedProps(
              cell.getCellProps([
                {
                  style: {
                    textAlign: cell.column.textAlign,
                    width: cell.column.width,
                  },
                },
              ]),
              'td'
            )
            return cell.column.tooltip ? (
              <div key={cellKey} {...cellProps}>
                <div
                  className="relative flex items-center mt-3"
                  style={{ justifyContent: cell.column.justifyContent }}
                >
                  <div
                    className="tooltip"
                    style={{ marginLeft: cell.column.ml, wordWrap: 'anywhere' }}
                  >
                    {cell.render('Cell')}
                  </div>
                  <span className="tooltiptext mx-auto bottom-[100%] w-[135px]">
                    {cell.column.tooltipText}
                  </span>
                </div>
              </div>
            ) : (
              <div key={cellKey} {...cellProps}>
                <div
                  className={`flex items-center ${cell.column.styleClasses}`}
                  style={{ justifyContent: cell.column.justifyContent }}
                >
                  <div
                    className="w-full"
                    style={{ marginLeft: cell.column.ml, wordWrap: 'anywhere' }}
                  >
                    {cell.render('Cell', { removeData: props.removeData })}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )
    },
    [prepareRow, shownRows, props.topOffset, props.removeData]
  )

  return (
    <>
      <div className={props.tableStyle ? props.tableStyle : ''}>
        {/* {columnHiding ? <div className="grid grid-cols-12"><img className="col-start-12 cols-end-13 w-1/2 max-w-[44px]" src={Settings} alt="Details" onClick={() => setIsModalOpen(true)}></img></div>:""} */}
        <div className={`tableWrap mx-auto ${widthProps?.tailwindWidth}`}>
          <div {...getTableProps()} className="table">
            {props.hideHeaders ? (
              <></>
            ) : (
              <div>
                {headerGroups.map((headerGroup) => {
                  const { key: headerGroupKey, props: headerGroupProps } =
                    splitKeyedProps(
                      headerGroup.getHeaderGroupProps([
                        { style: { width: '100%' } },
                      ]),
                      'tr'
                    )

                  return (
                    <div key={headerGroupKey} {...headerGroupProps}>
                      {headerGroup.headers.map((column) => {
                        const headerOptions = [
                          {
                            className: 'h-10',
                            style: {
                              textAlign: column.textAlign,
                              width: column.width,
                            },
                          },
                        ]
                        const generatedHeaderProps = column.isSortable
                          ? column.getHeaderProps(
                              column.getSortByToggleProps(headerOptions)
                            )
                          : column.getHeaderProps(headerOptions)
                        const { key: headerKey, props: headerProps } =
                          splitKeyedProps(generatedHeaderProps, 'th')

                        return (
                          <div key={headerKey} {...headerProps}>
                            <div
                              className={
                                `flex items-center ` +
                                `${column.headerStyleClasses}` +
                                ' h-full ' +
                                (column.hiddenHeader ? '' : '')
                              }
                              style={{ justifyContent: column.justifyContent }}
                            >
                              <div
                                style={{
                                  marginLeft: column.ml,
                                  display: 'flex',
                                  alignItems: 'center',
                                }}
                              >
                                {column.render('Header')}
                                {column.isSortable ? (
                                  <span>
                                    {column.isSorted ? (
                                      column.isSortedDesc ? (
                                        <img
                                          className="h-8 mr-1"
                                          src={ArrowDown}
                                          alt={'down'}
                                        />
                                      ) : (
                                        <img
                                          className="h-8 mr-1"
                                          src={ArrowUp}
                                          alt={'up'}
                                        />
                                      )
                                    ) : (
                                      ''
                                    )}
                                  </span>
                                ) : null}
                              </div>
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  )
                })}
              </div>
            )}
            <div {...getTableBodyProps({ className: props.tableClass })}>
              <AutoSizer disableWidth>
                {({ height }) =>
                  widthProps ? (
                    <FixedSizeList
                      className="hideOverflowX"
                      innerElementType={innerElementType}
                      width={widthProps.width}
                      height={height}
                      itemCount={rows.slice(0, props.showCount).length}
                      itemSize={props.height}
                    >
                      {RenderRow}
                    </FixedSizeList>
                  ) : (
                    <FixedSizeList
                      height={height}
                      itemCount={rows.slice(0, props.showCount).length}
                      itemSize={props.height}
                    >
                      {RenderRow}
                    </FixedSizeList>
                  )
                }
              </AutoSizer>
            </div>
            {props.hideFooters ? (
              <></>
            ) : (
              <div>
                {footerGroups.map((footerGroup) => {
                  const { key: footerGroupKey, props: footerGroupProps } =
                    splitKeyedProps(
                      footerGroup.getFooterGroupProps([
                        { style: { width: '100%' } },
                      ]),
                      'tr'
                    )

                  return (
                    <div key={footerGroupKey} {...footerGroupProps}>
                      {footerGroup.headers.map((column) => {
                        const { key: footerKey, props: footerProps } =
                          splitKeyedProps(
                            column.getHeaderProps([
                              {
                                className: 'h-10',
                                style: {
                                  textAlign: column.textAlign,
                                  width: column.width,
                                },
                              },
                            ]),
                            'th'
                          )

                        return (
                          <div key={footerKey} {...footerProps}>
                            <div
                              className={
                                `flex items-center ` +
                                `${column.footerStyleClasses}` +
                                ' h-full ' +
                                (column.hiddenHeader ? '' : '')
                              }
                              style={{ justifyContent: column.justifyContent }}
                            >
                              <div style={{ marginLeft: column.ml }}>
                                {column.render('Footer')}
                              </div>
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </div>
      </div>
      {isModalOpen && (
        <Modal setIsModalOpen={setIsModalOpen} backGroundClass={'darkBG'}>
          <ColumnHiding setIsModalOpen={setIsModalOpen} columns={allColumns} />
        </Modal>
      )}
    </>
  )
}
export default Table
